import { AnyFieldApi } from "@tanstack/react-form";

import { FieldState } from "@/dls/AppField";

type FieldErrorState = {
  state: FieldState;
  stateMessage: string;
};

const getError = (error: any) => {
  if (typeof error === "string") {
    return error;
  }
  return error.message;
};

export const getFieldErrorState = (
  field: AnyFieldApi
): FieldErrorState | undefined => {
  const [error] = field.state.meta.errors;

  if (error) {
    return {
      state: "invalid",
      stateMessage: getError(error),
    };
  }
};

// Google-style live username check for the create-account forms: returns a
// TanStack Form `onBlurAsync` validator. A taken name resolves to the
// localized message, which renders under the field (getFieldErrorState) and
// flips the field into error state — the forms' save buttons key on that.
// The single home of this rule so every create form probes identically.
export const createUsernameAvailabilityValidator =
  (tValidation: (key: string) => string) =>
  async ({ value }: { value: unknown }): Promise<string | undefined> => {
    const username = String(value ?? "").trim();
    // Too short to be a legal username — let the sync schema complain instead.
    if (username.length < 3) return undefined;
    try {
      const { checkUsernameAvailability } = await import("@/api/users");
      return (await checkUsernameAvailability(username))
        ? undefined
        : tValidation("username_taken");
    } catch {
      // The probe is a UX nicety — on failure defer to the server-side
      // uniqueness error at submit time instead of blocking the form.
      return undefined;
    }
  };

export const getDirtyValues = <T extends Record<string, any>>(
  formApi: any,
  allValues: T
): Partial<T> => {
  const dirtyValues: Partial<T> = {};
  for (const key in allValues) {
    const fieldMeta = formApi.getFieldMeta(key);
    if (fieldMeta?.isDirty) {
      const value = allValues[key];
      if (value !== null) {
        dirtyValues[key as keyof T] = value;
      }
    }
  }
  return dirtyValues;
};
